import { useEffect, useId, useRef, useState } from "react";
import type { Dispatch, ReactElement, SetStateAction } from "react";
import type { ApiClient, AdressAnsicht, AdressAnlegen, AdressBesitzer,
              AdressListe as AdressListeAntwort } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useBeforeUnload, useFormularZustand }
  from "@kundeninfo/ui";
import { Auswahl, Formular, Hinweis, Optionen, Schalter } from "@kundeninfo/ui/me-design";
import { KiKnopf, LoeschKnopf } from "../components/meHuellen.js";
import { Ortszeile } from "../components/Ortszeile.js";
import { useLaender } from "../laender.js";
import "./AdressListe.css";

/**
 * Das PANEL einer Adresse — Anlegen und Ändern im strikten Modalpanel, herausgelöst aus
 * `AdressListe.tsx` (Rechnungsangaben, Aufgabe 7), damit auch die Belegparteien es öffnen
 * können, ohne die Liste mitzubringen. Es bringt den CSS-Block `adr__` mit (Festlegung 9).
 *
 * Wie die Liste kennt es die Fachlichkeit NICHT: Die wählbaren Arten und ihre Rollen kommen
 * vom Server (`arten`), jeder Befund des Servers findet hier seinen Weg an ein Feld.
 */

/** Eine Art samt ihren zulässigen Rollen, so wie der Server sie mitschickt. */
export type AdressArt = AdressListeAntwort["arten"][number];
type AdressKind = AdressAnsicht["kind"];
type AdressRolle = AdressAnsicht["role"];
type ZeilenArt = AdressAnsicht["lines"][number]["kind"];

/** Nur die Klientenmethoden, die das Panel braucht. */
export type AdressPanelClient = Pick<ApiClient,
  "listAddresses" | "createAddress" | "updateAddress"
  // Die Ortszeile (Spec „Adresszeile", R17): Länderkürzel und PLZ-Verzeichnis.
  | "listLaender" | "listPlzOrte">;

/** Eine Zeile der Adressart-Auswahl (Entscheid 336): die Art mit ihren Rollen und ihr Optionswert —
 *  die Grundart heißt wie ihr `key`, die abgespaltene Wahl `${key}_${rolle}` (Festlegung 8). */
export type AdressWahl = { wert: string; art: AdressArt; abgespalten: boolean };

export function adressWahlen(arten: readonly AdressArt[]): AdressWahl[] {
  return arten.map((art) => {
    const abgespalten = art.rollen.length === 1 && arten.some((b) => b !== art && b.key === art.key);
    return { art, abgespalten, wert: abgespalten ? `${art.key}_${art.rollen[0]}` : art.key };
  });
}

/** Die Wahl einer Adresse: Art und Rolle passen, sonst die erste Wahl ihrer Art (Grundart). */
export function wahlFuer(
  wahlen: readonly AdressWahl[], kind: AdressKind, role: AdressRolle,
): AdressWahl | undefined {
  return wahlen.find((w) => w.art.key === kind && w.art.rollen.includes(role))
    ?? wahlen.find((w) => w.art.key === kind);
}

export type AdressPanelProps = {
  client: AdressPanelClient;
  besitzer: AdressBesitzer;
  ownerId: string;
  arten: AdressArt[];
  /** Ändern: die Vorlage. Anlegen: `null`. */
  vorlage: AdressAnsicht | null;
  /** Anlegen: Optionswert der vorgewählten Wahl (z. B. `POSTAL_BILLING`); ohne Angabe die erste wählbare. */
  startWahl?: string;
  /** Der Name des Objekts — Vorbelegung der ersten Anschriftzeile einer neuen Postanschrift. */
  namensVorschlag?: string;
  /** Nach dem Speichern — mit der Antwort des Schreibwegs (`createAddress`/`updateAddress`). */
  onGespeichert: (gespeichert: AdressAnsicht) => void;
  onAbbrechen: () => void;
  /** Nach „Neu laden" (409): die frische Liste, damit der Einbauort sie übernimmt. Fehlt
   *  die geänderte Adresse darin, schliesst der Einbauort das Panel und sagt warum. */
  onListe?: (antwort: AdressListeAntwort) => void;
};

/**
 * Die deutschen Namen der ROLLEN — reine Beschriftung, deshalb hier und nicht im Vertrag.
 *
 * Der `Record` über `AdressRolle` ist zugleich der Typtest: Kommt in `api-client` eine
 * Rolle hinzu, fehlt hier ein Schlüssel und die Typprüfung schlägt an, statt dass die
 * Maske still den rohen Schlüssel („INVOICE_RECIPIENT") anzeigt. Dasselbe Muster wie bei
 * den Weltnamen der Umgebung. Die Wörter decken sich mit denen der Domäne
 * (`ROLLEN_NAME` in addressRules.ts) — ein Wort für eine Sache, auch über die Pakete hinweg.
 */
const ROLLEN_NAME: Record<AdressRolle, string> = {
  MAIN: "Haupt", BILLING: "Rechnung", DELIVERY: "Lieferung",
  POSTAL_MAIL: "Postweg", INVOICE_RECIPIENT: "Rechnungsempfänger",
  GENERAL: "Allgemein",
};

export function rollenName(r: AdressRolle): string {
  return ROLLEN_NAME[r];
}

/** Die Zeilenarten des Anschriftblocks, in der Reihenfolge, in der sie im Block stehen:
 *  erst die Organisation, dann die Person, dazwischen das c/o. Auch hier der Typtest. */
const ZEILENART_NAME: Record<ZeilenArt, string> = {
  ORGANIZATION: "Firmenname",
  UNIT: "Organisationseinheit",
  CARE_OF: "c/o",
  PERSON: "Person",
  FUNCTION: "Funktion",
};
const ZEILENARTEN = Object.keys(ZEILENART_NAME) as ZeilenArt[];

/** Nur UNIT-Zeilen gehen in die E-Rechnung (BT-51/BT-163), und höchstens zwei davon —
 *  die Maske sperrt, was der Server ohnehin mit 422 abwiese. */
const XML_ZEILEN_MAX = 2;
/** Der eine Wert des Kennzeichens „In E-Rechnung“ (me-optionen mit einer Option). */
const IN_XML = "ja";

/**
 * Die Beschriftung des Wertfeldes je Art — sie sagt, WAS hineingehört.
 *
 * Ein VOLLSTÄNDIGER `Record` über alle Arten ausser der Postanschrift (die hat kein
 * Wertfeld, sondern einen Anschriftblock): Kommt in `api-client` eine Art hinzu, fehlt hier
 * ein Schlüssel und die Typprüfung schlägt an — statt dass die Maske still „Wert"
 * beschriftet und niemand merkt, dass die neue Art keine eigene Ansprache hat. Dasselbe
 * gilt für den Hilfetext darunter; `null` heisst dort „braucht keinen", und das ist eine
 * ENTSCHEIDUNG je Art, kein vergessener Eintrag.
 */
type WertArt = Exclude<AdressKind, "POSTAL">;

const WERT_LABEL: Record<WertArt, string> = {
  EMAIL: "E-Mail", PHONE: "Telefon", WEB: "Webseite", ILN: "ILN (GLN)",
  PURCHASE_ORDER: "Bestellnummer",
};

const WERT_HILFE: Record<WertArt, string | null> = {
  EMAIL: null,
  PHONE: "International und ohne Leerzeichen, z. B. +494055441200.",
  WEB: "Vollständig mit https://.",
  ILN: "13 Ziffern; die letzte ist die Prüfziffer (GS1).",
  PURCHASE_ORDER: null,
};

/** Die Postanschrift erreicht diese beiden Wege nie (sie zeichnet den Anschriftblock);
 *  der Rückfall steht trotzdem da, damit die Maske ohne Behauptung auskommt. */
function wertLabel(kind: AdressKind): string {
  return kind === "POSTAL" ? "Wert" : WERT_LABEL[kind];
}
function wertHilfe(kind: AdressKind): string | null {
  return kind === "POSTAL" ? null : WERT_HILFE[kind];
}

type ZeilenEntwurf = { kind: ZeilenArt; text: string; inInvoiceXml: boolean };

/** Der Entwurf im Panel. Die Textfelder sind Zeichenketten — der Schreibweg macht daraus
 *  `null`, wie überall. */
type Entwurf = {
  /** Beim Ändern die Kennung der Verknüpfung, beim Anlegen `null`. */
  id: string | null;
  version: number;
  kind: AdressKind;
  role: AdressRolle;
  value: string;
  label: string;
  note: string;
  validFrom: string;
  street: string;
  houseNumber: string;
  postalCode: string;
  city: string;
  country: string;
  personFirst: boolean;
  lines: ZeilenEntwurf[];
};

/**
 * Die Felder der Maske, an die ein Befund des Servers gehen kann.
 *
 * `kind` steht bewusst NICHT darin, obwohl der Server es beanstanden kann (422
 * ART_UNVERAENDERLICH): Die Art ist beim Ändern ein gesperrtes Feld und beim Anlegen eine
 * Auswahl aus genau dem, was der Server mitgeschickt hat — ein Befund daran ist kein
 * Eingabefehler, sondern ein Widerspruch zwischen Maske und Server. Der gehört an die
 * Fussleiste, wo er als Satz gelesen wird, und nicht als roter Strich unter ein Feld, an
 * dem sich nichts richten lässt.
 */
type FeldName = "role" | "value" | "label" | "note" | "validFrom"
  | "street" | "houseNumber" | "postalCode" | "city" | "country" | "lines";

const FELDNAMEN: readonly FeldName[] = [
  "role", "value", "label", "note", "validFrom",
  "street", "houseNumber", "postalCode", "city", "country", "lines",
];

/** Der leere Entwurf beim Anlegen: die Wahl `startWahl`, sonst — auch bei einem `startWahl`, den
 *  dieser Besitzer nicht anbietet — die erste wählbare (Postanschrift). */
function leererEntwurf(wahlen: readonly AdressWahl[], startWahl: string | undefined,
                       namensVorschlag: string | undefined): Entwurf {
  const waehlbar = wahlen.filter((w) => w.art.rollen.length > 0);
  const wahl = waehlbar.find((w) => w.wert === startWahl) ?? waehlbar[0];
  return {
    id: null, version: 0,
    kind: wahl?.art.key ?? "POSTAL", role: wahl?.art.rollen[0] ?? "MAIN",
    value: "", label: "", note: "", validFrom: "",
    street: "", houseNumber: "", postalCode: "", city: "", country: "DE",
    personFirst: false,
    // Eine Postanschrift beginnt mit dem Namen des Objekts: Er steht auf jedem Brief, und
    // ihn abzutippen wäre die erste Gelegenheit, ihn anders zu schreiben als oben im Kopf.
    lines: wahl?.art.hatPostanschrift === true
      ? [{ kind: "ORGANIZATION", text: namensVorschlag ?? "", inInvoiceXml: false }]
      : [],
  };
}

function entwurfAus(a: AdressAnsicht): Entwurf {
  return {
    id: a.id, version: a.version, kind: a.kind, role: a.role,
    value: a.value ?? "", label: a.label ?? "", note: a.note ?? "",
    validFrom: a.validFrom ?? "",
    street: a.postal?.street ?? "", houseNumber: a.postal?.houseNumber ?? "",
    postalCode: a.postal?.postalCode ?? "",
    city: a.postal?.city ?? "", country: a.postal?.country ?? "DE",
    personFirst: a.postal?.personFirst ?? false,
    lines: a.lines.map((z) => ({ kind: z.kind, text: z.text,
                                 inInvoiceXml: z.inInvoiceXml })),
  };
}

function leer(wert: string): string | null {
  return wert.trim() === "" ? null : wert.trim();
}

/**
 * Was der Schreibweg schickt — und die Spur zurück.
 *
 * Leere Zeilen fallen heraus (die Maske hält sie als Eingabeplatz vor, gespeichert wären
 * sie eine Lücke im Block). Damit stimmen die Plätze im Rumpf nicht mehr mit denen in der
 * Maske überein — und genau darauf zeigt der Server, wenn er `lines[1].inInvoiceXml`
 * beanstandet. `zeilenPlatz[i]` sagt deshalb, welche Zeile der MASKE die i-te Zeile des
 * Rumpfes war; ohne diese Spur landete ein Befund an der falschen Zeile, und zwar ohne
 * jedes Anzeichen.
 */
function alsEingabe(e: Entwurf, hatPostanschrift: boolean):
  { eingabe: AdressAnlegen; zeilenPlatz: number[] } {
  const zeilenPlatz: number[] = [];
  const lines: AdressAnlegen["lines"] = [];
  if (hatPostanschrift) {
    e.lines.forEach((z, i) => {
      if (z.text.trim() === "") return;
      lines.push({ kind: z.kind, text: z.text.trim(), sortIndex: lines.length,
                   inInvoiceXml: z.inInvoiceXml });
      zeilenPlatz.push(i);
    });
  }
  return {
    eingabe: {
      kind: e.kind, role: e.role,
      value: hatPostanschrift ? null : leer(e.value),
      label: leer(e.label), note: leer(e.note), validFrom: leer(e.validFrom),
      postal: hatPostanschrift ? {
        street: leer(e.street), houseNumber: leer(e.houseNumber),
        postalCode: leer(e.postalCode), city: leer(e.city),
        country: e.country, personFirst: e.personFirst,
      } : null,
      lines,
    },
    zeilenPlatz,
  };
}

/**
 * Wie diese Adresse in einer Rückfrage und in einem zugänglichen Namen heisst — die
 * UNTERSCHEIDENDE Angabe zuerst.
 *
 * Die Reihenfolge ist nicht beliebig: Ein Objekt hat mehrere Adressen derselben Art, und
 * der Name muss sagen, WELCHE gemeint ist. Die Bezeichnung („Zentrale", „Lager Süd") ist
 * genau dafür vergeben worden und steht deshalb vorn. Dann der Wert (jede E-Mail ist eine
 * andere). Bei einer Postanschrift wäre die erste Zeile der FIRMENNAME — und der ist an
 * allen Anschriften desselben Kunden gleich: „Nordlicht Logistik GmbH archivieren" stünde
 * dann dreimal auf dem Bildschirm. Also Strasse und Ort, die Angaben, in denen sich zwei
 * Anschriften tatsächlich unterscheiden.
 */
export function bezeichnung(a: AdressAnsicht): string {
  if (a.label !== null) return a.label;
  if (a.value !== null) return a.value;
  if (a.postal !== null) {
    const ort = [a.postal.street, a.postal.city]
      .filter((t): t is string => t !== null && t !== "").join(", ");
    if (ort !== "") return ort;
  }
  return a.zeilen[0] ?? "Adresse ohne Angabe";
}

/**
 * Der Befund des Servers an seinen Platz in der Maske.
 *
 * Der Server benennt seine Felder so, wie der RUMPF gebaut ist: `city` flach,
 * `postal.city` unter dem Schema, `lines[1].inInvoiceXml` an einer bestimmten Zeile.
 * Diese Funktion kennt alle drei Formen; was sie nicht unterbringt, sagt sie mit `null` —
 * der Aufrufer stellt es dann an die Fussleiste, damit NICHTS unsichtbar bleibt.
 */
function zielFeld(name: string, zeilenPlatz: number[]):
  { feld: FeldName } | { zeile: number } | null {
  const zeile = /^lines\[(\d+)\]/.exec(name);
  if (zeile !== null) {
    const platz = zeilenPlatz[Number(zeile[1])];
    return platz === undefined ? null : { zeile: platz };
  }
  const flach = name.startsWith("postal.") ? name.slice("postal.".length) : name;
  return (FELDNAMEN as readonly string[]).includes(flach)
    ? { feld: flach as FeldName } : null;
}

export function AdressPanel(p: AdressPanelProps): ReactElement {
  const { client, besitzer, ownerId } = p;
  const wahlen = adressWahlen(p.arten);
  const [dialog, setDialog] = useState<Entwurf>(() => (p.vorlage === null
    ? leererEntwurf(wahlen, p.startWahl, p.namensVorschlag) : entwurfAus(p.vorlage)));
  const [ausgang, setAusgang] = useState<Entwurf>(dialog);
  /** Der Platz jeder mitgeschickten Zeile in der Maske (aus `alsEingabe`) — damit ein
   *  Befund `lines[n]` an die richtige ZEILE findet, auch wenn leere Zeilen fehlten. */
  const zeilenPlatz = useRef<number[]>([]);
  /** „Meine Eingaben behalten“: nur die Versionsbasis vorrücken (siehe KundeStammdaten). */
  const eingabenBehalten = useRef(false);
  /** Der Riegel gegen Antworten, die erst nach dem Schliessen eintreffen: Ein abgeräumtes
   *  Panel (Besitzerwechsel) meldet nichts mehr an seinen Einbauort. */
  const aktiv = useRef(true);
  useEffect(() => {
    aktiv.current = true;
    return () => { aktiv.current = false; };
  }, []);
  /** Eigene Kennungen je Einbauort: Das Panel steht an mehreren Stellen, und zwei gleiche
   *  `id`-Werte auf einer Seite brächen die Verbindung von Beschriftung und Feld. */
  const idp = useId();

  const wahl = wahlFuer(wahlen, dialog.kind, dialog.role);
  const art = wahl?.art;
  const hatPostanschrift = art?.hatPostanschrift === true;
  /** Nur die Postanschrift hat eine Ortszeile — E-Mail, Telefon und Web brauchen die Länderkürzel nicht. */
  const laender = useLaender(hatPostanschrift ? client.listLaender : undefined);
  const rollenWaehlbar = (art?.rollen.length ?? 0) > 1;
  // Anlegen: jede wählbare Wahl. Ändern: die Art bleibt (422 ART_UNVERAENDERLICH), gewechselt werden darf nur
  // zwischen Wahlen DERSELBEN Art — ein Rollenwechsel, den updateAddress samt Hauptadress-Regeln kann.
  const angebot = wahlen.filter((w) => w.art.rollen.length > 0
    && (dialog.id === null || w.art.key === dialog.kind));

  /**
   * Formularzustände des Panels (ME-Etappe 3, Aufgabe 3): `laeuft` sperrt Felder und Fuß,
   * eine 422 legt ihre Befunde an Feld oder ZEILE (`zielFeld`) und der Fokus springt auf
   * den ersten — auf einem Telefon liegt er oft ausserhalb des Bildes, ohne den Sprung stünde
   * die Maske scheinbar unverändert da. Was die Maske nicht unterbringt (`kind` beim
   * Ändern, unbekannte Felder, `role` bei einer Wahl mit nur einer Rolle), steht als Satz im
   * Panel: NICHTS verschwindet still. Eine 409 beim Ändern hält die Eingabe als „fremder
   * Stand“ fest (Neu laden / Meine Eingaben behalten).
   */
  const formular = useFormularZustand<AdressAnsicht>({
    speichern: async () => {
      const e = dialog;
      const { eingabe, zeilenPlatz: platz } = alsEingabe(e, hatPostanschrift);
      zeilenPlatz.current = platz;
      return e.id === null
        ? client.createAddress(besitzer, ownerId, eingabe)
        : client.updateAddress(besitzer, ownerId, e.id, { ...eingabe, version: e.version });
    },
    neuLaden: async () => {
      const behalten = eingabenBehalten.current;
      eingabenBehalten.current = false;
      const id = dialog.id;
      const frisch = await client.listAddresses(besitzer, ownerId);
      if (!aktiv.current) return;
      p.onListe?.(frisch);
      const neu = frisch.items.find((x) => x.id === id);
      // Gibt es die Adresse nicht mehr, schliesst der Einbauort das Panel (`onListe`).
      if (neu === undefined) return;
      if (behalten) {
        setDialog((d) => ({ ...d, version: neu.version }));
      } else {
        setDialog(entwurfAus(neu));
        setAusgang(entwurfAus(neu));
      }
    },
    bekannteFelder: (feld) => (feld !== "role" || rollenWaehlbar)
      && zielFeld(feld, zeilenPlatz.current) !== null,
  });
  const speichert = formular.laeuft;
  // Die Befunde der 422 an ihren Platz in der Maske (Feld oder Zeile, siehe `zielFeld`).
  const feldFehler: Partial<Record<FeldName, string>> = {};
  const zeilenFehler: Record<number, string> = {};
  for (const [name, text] of Object.entries(formular.feldFehler)) {
    const ziel = zielFeld(name, zeilenPlatz.current);
    if (ziel === null) continue;
    if ("feld" in ziel) feldFehler[ziel.feld] ??= text;
    else zeilenFehler[ziel.zeile] ??= text;
  }

  const geaendert = JSON.stringify(dialog) !== JSON.stringify(ausgang);
  useBeforeUnload(geaendert);

  /** Der EINE Ausgang neben dem Speichern (Hausregel „striktes Modalpanel"): kein Ausgang
   *  unter laufendem Schreiben, Rückfrage bei ungespeicherten Eingaben. */
  async function abbrechen(): Promise<void> {
    if (speichert) return;
    if (!(await darfAbbrechen(geaendert))) return;
    p.onAbbrechen();
  }

  async function beiSpeichern(): Promise<void> {
    const gespeichert = await formular.aktion();
    if (gespeichert === undefined || !aktiv.current) return;
    p.onGespeichert(gespeichert);
  }

  function eigeneEingabenBehalten(): void {
    eingabenBehalten.current = true;
    void formular.neuLaden();
  }

  return (
    <Modal titel={dialog.id === null ? "Adresse hinzufügen" : "Adresse ändern"}
      onAbbrechen={abbrechen} abbrechenGesperrt={speichert} breite="mittel">
      {formular.meldung !== null && <Hinweis art="fehler">{formular.meldung}</Hinweis>}
      <Formular beschriftung="oben" {...formular.formularProps}
        hinweis={formular.zustand === "fremder-stand" ? (
          <span>
            Diese Adresse wurde zwischenzeitlich geändert. Deine Eingaben stehen noch da —
            „Neu laden“ übernimmt den aktuellen Stand.{" "}
            <KiKnopf className="ki-eingaben-behalten"
              onClick={eigeneEingabenBehalten}>
              Meine Eingaben behalten
            </KiKnopf>
          </span>
        ) : undefined}>
        {/* Beim Ändern bleibt die Art, was sie ist — der Server weist einen Wechsel mit
            422 ART_UNVERAENDERLICH ab. Gewechselt werden darf nur zwischen den Wahlen DIESER
            Art (Postanschrift ↔ Rechnungsadresse); bleibt keine zweite, ist das Feld gesperrt. */}
        <Auswahl id={`${idp}-kind`} name={`${idp}-kind`} beschriftung="Adressart"
          wert={wahl?.wert ?? ""} gesperrt={angebot.length < 2}
          onGeaendert={(ev) => {
            const neu = wahlen.find((w) => w.wert === ev.wert);
            if (neu === undefined) return;
            setDialog((d) => ({
              ...d, kind: neu.art.key,
              // Zurück zur Wahl, mit der das Panel aufging: wieder die Rolle von damals
              // (z. B. Lieferung), nicht die erste der Art.
              role: neu.art.key === ausgang.kind && neu.art.rollen.includes(ausgang.role)
                ? ausgang.role : neu.art.rollen[0] ?? "MAIN",
              // Die Zeilen gehören an eine Postanschrift; eine E-Mail-Adresse mit
              // Anschriftzeilen wiese der Server ab (`zeilenFehler` in domain).
              lines: neu.art.hatPostanschrift
                ? (d.lines.length > 0 ? d.lines
                  : [{ kind: "ORGANIZATION", text: p.namensVorschlag ?? "", inInvoiceXml: false }])
                : [],
            }));
          }}>
          {angebot.map((w) => <option key={w.wert} value={w.wert}>{w.art.name}</option>)}
        </Auswahl>

        {/* Entscheid 336: Das Feld „Rolle" steht nur, wenn die Wahl mehr als eine Rolle trägt. */}
        {rollenWaehlbar && (
          <Auswahl id={`${idp}-role`} name={`${idp}-role`} beschriftung="Rolle"
            wert={dialog.role}
            {...(feldFehler.role !== undefined ? { fehler: feldFehler.role } : {})}
            onGeaendert={(ev) => {
              const wert = ev.wert as AdressRolle;
              setDialog((d) => ({ ...d, role: wert }));
            }}>
            {art!.rollen.map((r) => <option key={r} value={r}>{rollenName(r)}</option>)}
          </Auswahl>
        )}

        {hatPostanschrift ? (
          <>
            {/* Straße und Hausnummer stehen NEBENEINANDER, nicht untereinander: Sie
                bilden auf dem Briefumschlag eine Zeile, und zwei volle Zeilen im
                Formular liessen sie wie zwei Anschriftteile aussehen. Auf schmalem
                Bildschirm bricht der Zweispalter um (siehe .adr__strassenzeile). */}
            <div className="adr__strassenzeile">
              <Feld id={`${idp}-street`} label="Straße" bearbeitbar
                wert={dialog.street}
                onChange={(w) => setDialog((d) => ({ ...d, street: w }))}
                {...(feldFehler.street !== undefined
                  ? { fehler: feldFehler.street } : {})} />
              <Feld id={`${idp}-houseNumber`} label="Hausnummer" bearbeitbar
                wert={dialog.houseNumber}
                onChange={(w) =>
                  setDialog((d) => ({ ...d, houseNumber: w }))}
                {...(feldFehler.houseNumber !== undefined
                  ? { fehler: feldFehler.houseNumber } : {})} />
            </div>
            {/* Land, PLZ und Ort in EINER Zeile (Spec „Adresszeile", E400) — derselbe Baustein wie in
                allen Adressmasken; die Feldnamen bleiben für die 422-Zuordnung (`zielFeld`) gleich.
                Der `key` bindet seinen inneren Stand (eigener Vorschlag, Eingabe, gemerkte Antworten)
                an Datensatz UND Fassung: „Neu laden" und Datensatzwechsel beginnen ihn neu (M-6a). */}
            <Ortszeile key={`${dialog.id ?? "neu"}:${dialog.version}`} praefix={idp}
              laender={laender} plzOrte={client.listPlzOrte}
              gesperrt={formular.zustand !== "bereit"}
              werte={{ country: dialog.country, postalCode: dialog.postalCode, city: dialog.city }}
              fehler={feldFehler}
              onAenderung={(teil) => setDialog((d) => ({ ...d, ...teil }))} />
            <Schalter id={`${idp}-personFirst`} name={`${idp}-personFirst`}
              beschriftung="Person über der Firma" an={dialog.personFirst}
              onGeaendert={(ev) =>
                setDialog((d) => ({ ...d, personFirst: ev.an }))} />
            {/* DIN 5008 setzt die Firma nach oben; persönliche Post dreht das um. */}
            <p className="adr__hilfetext">
              Ohne Haken steht der Firmenblock oben (DIN 5008).
            </p>
            <AnschriftZeilen zeilen={dialog.lines} idp={idp} setDialog={setDialog}
              fehler={feldFehler.lines} zeilenFehler={zeilenFehler} />
          </>
        ) : (
          <>
            <Feld id={`${idp}-value`} label={wertLabel(dialog.kind)}
              bearbeitbar wert={dialog.value}
              onChange={(w) => setDialog((d) => ({ ...d, value: w }))}
              {...(feldFehler.value !== undefined
                ? { fehler: feldFehler.value } : {})} />
            {wertHilfe(dialog.kind) !== null && (
              <p className="adr__hilfetext">{wertHilfe(dialog.kind)}</p>)}
          </>
        )}

        <Feld id={`${idp}-label`} label="Bezeichnung" bearbeitbar wert={dialog.label}
          onChange={(w) => setDialog((d) => ({ ...d, label: w }))}
          {...(feldFehler.label !== undefined ? { fehler: feldFehler.label } : {})} />
        <Feld id={`${idp}-validFrom`} label="Gültig ab" typ="date" bearbeitbar
          wert={dialog.validFrom}
          onChange={(w) => setDialog((d) => ({ ...d, validFrom: w }))}
          {...(feldFehler.validFrom !== undefined
            ? { fehler: feldFehler.validFrom } : {})} />
        <Feld id={`${idp}-note`} label="Notiz" bearbeitbar wert={dialog.note}
          onChange={(w) => setDialog((d) => ({ ...d, note: w }))}
          {...(feldFehler.note !== undefined ? { fehler: feldFehler.note } : {})} />
      </Formular>

      {/* Die zwei einzigen Ausgänge. `abbrechenGesperrt` steht an BEIDEN Stellen — am
          Rahmen für Escape, hier für den Knopf. */}
      <ModalFuss speichernText="Speichern"
        speichernGesperrt={formular.zustand !== "bereit"}
        abbrechenGesperrt={speichert}
        onSpeichern={() => { void beiSpeichern(); }} onAbbrechen={abbrechen} />
    </Modal>
  );
}

type AnschriftZeilenProps = {
  zeilen: ZeilenEntwurf[];
  idp: string;
  setDialog: Dispatch<SetStateAction<Entwurf>>;
  /** Ein Befund, der den BLOCK als ganzen betrifft (zu viele Kreuze, unbekannte Art). */
  fehler: string | undefined;
  /** Befunde an einzelnen Zeilen, am Platz der MASKE (siehe `alsEingabe`). */
  zeilenFehler: Record<number, string>;
};

/**
 * Der Anschriftblock im Panel — die Zeilen über Straße und Ort: Firmenname,
 * Organisationseinheit, c/o, Person, Funktion.
 *
 * Er ist eine Liste und keine feste Zahl von Feldern, weil eine Anschrift nicht zwei
 * Zusatzzeilen hat, sondern so viele, wie der Brief braucht („Abteilung Einkauf", „z. Hd.
 * Frau Meyer", „c/o Nordlicht Verwaltung"). Die REIHENFOLGE in der Maske ist die im Block:
 * Was hier oben steht, steht auf dem Brief oben (`sortIndex` vergibt der Schreibweg
 * lückenlos neu, siehe `alsEingabe`).
 *
 * Die Beschriftungen der Spalten stehen EINMAL über der Liste; die Felder selbst tragen
 * ihren zugänglichen Namen mitsamt der Zeilennummer, damit ein Bildschirmleser „Text der
 * Zeile 2" sagt und nicht dreimal „Text".
 */
function AnschriftZeilen(p: AnschriftZeilenProps): ReactElement {
  const imXml = p.zeilen.filter((z) => z.inInvoiceXml).length;

  function aendern(i: number, teil: Partial<ZeilenEntwurf>): void {
    p.setDialog((d) => ({
      ...d,
      lines: d.lines.map((z, j) => {
        if (j !== i) return z;
        const neu = { ...z, ...teil };
        // Das Kennzeichen hängt an der ORGANISATIONSEINHEIT (BT-51/BT-163). Wandert die
        // Zeile zu einer anderen Art, wandert es nicht mit: Es bliebe sonst unsichtbar
        // gesetzt (das Kästchen ist dann gesperrt) und liefe in eine 422.
        return neu.kind === "UNIT" ? neu : { ...neu, inInvoiceXml: false };
      }),
    }));
  }

  return (
    <div className="adr__zeilenblock">
      <h5 className="adr__blocktitel">Anschriftzeilen</h5>
      <div className="adr__zeilenkopf" aria-hidden="true">
        <span>Zeilenart</span><span>Text</span><span /><span />
      </div>
      {p.zeilen.map((z, i) => (
        // Der Platz IST die Kennung: Eine Zeile hat keine eigene, und die Reihenfolge ist
        // gerade das, was hier bearbeitet wird.
        <div key={i} className="adr__zeilenentwurf" role="group"
          aria-label={`Anschriftzeile ${i + 1}`}>
          <Auswahl kompakt beschriftung={`Zeilenart der Zeile ${i + 1}`} wert={z.kind}
            onGeaendert={(ev) => aendern(i, { kind: ev.wert as ZeilenArt })}>
            {ZEILENARTEN.map((k) => (
              <option key={k} value={k}>{ZEILENART_NAME[k]}</option>))}
          </Auswahl>
          {/* Übergang P12-7 (docs/ME-DESIGN.md), seit ME-Etappe 3: der Text der Zeile bleibt
              ein rohes Feld —
              im Zeilenraster steht die Beschriftung EINMAL über der Spalte, und me-eingabe
              zeigt ihre Beschriftung immer sichtbar (kein `kompakt`).
              Der Befund hängt per aria-invalid am Text; dorthin springt der Fokus
              (me-formular `zumErstenFehler`) — bei einem Befund am ganzen Block an die
              erste Zeile, die nächstgelegene Stelle, an der sich etwas ändern lässt. */}
          <input id={`${p.idp}-line-${i}-text`} className="ki-feld__eingabe" type="text"
            value={z.text} aria-label={`Text der Zeile ${i + 1}`}
            aria-invalid={p.zeilenFehler[i] !== undefined
              || (i === 0 && p.fehler !== undefined) ? true : undefined}
            onChange={(ev) => aendern(i, { text: ev.target.value })} />
          {/* Das Kennzeichen ist seit P17 ein `Optionen mehrfach` mit EINER Option
              (me-optionen, Entscheid 40): die Gruppe heisst „Zeile n in die E-Rechnung“, das
              Kästchen „In E-Rechnung“ (sichtbar, auch am Telefon ohne Spaltenkopf).
              Nicht wählbar heisst gesperrt — der Fokus bleibt, die Vorlesestimme hört
              „nicht verfügbar“; me-optionen kennt kein disabled. */}
          <Optionen mehrfach aria-label={`Zeile ${i + 1} in die E-Rechnung`}
            wert={z.inInvoiceXml ? [IN_XML] : []}
            {...(z.kind !== "UNIT" || (imXml >= XML_ZEILEN_MAX && !z.inInvoiceXml)
              ? { gesperrt: true } : {})}
            onGeaendert={(ev) => aendern(i, { inInvoiceXml: (ev.werte ?? []).includes(IN_XML) })}>
            <option value={IN_XML}>In E-Rechnung</option>
          </Optionen>
          <LoeschKnopf className="adr__knopf"
            name={`Zeile ${i + 1} entfernen`}
            onClick={() => p.setDialog((d) => ({ ...d, lines: d.lines.filter((_, j) => j !== i) }))} />
          {p.zeilenFehler[i] !== undefined && (
            <span className="ki-feld__fehler adr__zeilenfehler">{p.zeilenFehler[i]}</span>)}
        </div>
      ))}
      <div className="adr__fuss">
        <KiKnopf className="adr__knopf"
          onClick={() => p.setDialog((d) => ({
            ...d,
            lines: [...d.lines, { kind: "UNIT", text: "", inInvoiceXml: false }],
          }))}>
          Zeile hinzufügen
        </KiKnopf>
      </div>
      <p className="adr__hilfetext">
        Das Kennzeichen „In E-Rechnung" steht nur an Zeilen der Organisationseinheit.
        {imXml >= XML_ZEILEN_MAX
          && " Die E-Rechnung trägt höchstens zwei Anschriftzeilen."}
      </p>
      {p.fehler !== undefined && (
        <span className="ki-feld__fehler">{p.fehler}</span>)}
    </div>
  );
}
